import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Home, BarChart3, Users, FileText, BookOpen, Phone, Video } from 'lucide-react';

const theme = {
  bg: '#F7F4EF',
  orange: '#E8621A',
  ink: '#1A1713',
  ink3: '#6B6358',
};

const GlobalNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  
  const navLinks = [
    { to: '/', label: 'Accueil', icon: Home },
    { to: '/diagnostic', label: 'Diagnostic', icon: FileText },
    { to: '/crm', label: 'CRM', icon: BarChart3, highlight: true },
    { to: '/equipe', label: 'Équipe', icon: Users, highlight: true },
    { to: '/recrutement', label: 'Recrutement', icon: Users },
    { to: '/onboarding', label: 'Onboarding', icon: BookOpen },
    { to: '/setter-guide', label: 'Guide DM', icon: FileText },
    { to: '/setter-call-script', label: 'Script Setter', icon: Phone },
    { to: '/closer-call-script', label: 'Script Closer', icon: Video },
  ];
  
  // SECURITY: navbar is HIDDEN by default. Only shown on explicit internal/team paths.
  // Anything not in this whitelist (including client pages, results, payment, app, etc.) → no navbar.
  const internalPaths = [
    '/crm',
    '/equipe',
    '/connexion',
    '/recrutement',
    '/onboarding',
    '/setter-guide',
    '/setter-call-script',
    '/closer-call-script',
    '/stats',
  ];
  const isInternalPath = internalPaths.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'));

  // Extra guard: only show internal navbar if the user is actually authenticated as team member.
  // Otherwise even if a curious client types /crm, they get bounced to /connexion without seeing internal links.
  let isAuthed = false;
  try {
    isAuthed = typeof window !== 'undefined' && window.localStorage.getItem('crm_unlocked') === 'true';
  } catch (e) { /* localStorage disabled */ }

  if (!isInternalPath || !isAuthed) {
    return null;
  }
  
  return (
    <>
      {/* Fixed hamburger button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed top-4 right-4 z-[100] p-3 rounded-full bg-white shadow-lg border border-gray-200 hover:shadow-xl transition-shadow"
        whileTap={{ scale: 0.95 }}
        data-testid="global-menu-btn"
      >
        {isOpen ? (
          <X className="w-6 h-6" style={{ color: theme.ink }} />
        ) : (
          <Menu className="w-6 h-6" style={{ color: theme.ink }} />
        )}
      </motion.button>
      
      {/* Overlay */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 bg-black/30 z-[90]"
          />
        )}
      </AnimatePresence>
      
      {/* Slide-out menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.nav
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed top-0 right-0 h-full w-72 bg-white shadow-2xl z-[95] overflow-y-auto"
          >
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center gap-2 mb-8 pt-2">
                <span className="font-bebas text-2xl" style={{ color: theme.ink }}>PRSNL</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: theme.orange, color: '#FFF' }}>
                  MENU
                </span>
              </div>
              
              {/* Navigation links */}
              <div className="space-y-2">
                {navLinks.map((link) => {
                  const Icon = link.icon;
                  const isActive = location.pathname === link.to;
                  
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      onClick={() => setIsOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                        isActive 
                          ? 'bg-orange-100 text-orange-600' 
                          : link.highlight 
                            ? 'bg-orange-50 text-orange-600 hover:bg-orange-100'
                            : 'hover:bg-gray-100 text-gray-700'
                      }`}
                      data-testid={`nav-link-${link.to.replace('/', '') || 'home'}`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{link.label}</span>
                      {link.highlight && (
                        <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full font-bold bg-orange-500 text-white">
                          CRM
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
              
              {/* Quick access section */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-3 px-4">
                  Accès rapide
                </p>
                <Link
                  to="/crm"
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-3 px-4 py-4 rounded-xl transition-all"
                  style={{ backgroundColor: 'rgba(232,98,26,0.1)' }}
                >
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: theme.orange }}
                  >
                    <BarChart3 className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-bold text-gray-800">Ouvrir le CRM</p>
                    <p className="text-xs text-gray-500">Gérer les leads</p>
                  </div>
                </Link>
              </div>
              
              {/* Footer */}
              <div className="mt-8 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    try {
                      localStorage.removeItem('crm_unlocked');
                      localStorage.removeItem('crm_member');
                    } catch (e) { /* ignore */ }
                    window.location.href = '/';
                  }}
                  className="w-full text-xs text-gray-500 hover:text-red-500 text-center py-2 transition-colors"
                  data-testid="navbar-logout-btn"
                >
                  Se déconnecter
                </button>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </>
  );
};

export default GlobalNavbar;
